import { BELOW_DESKTOP_MEDIA, PHONE_MEDIA } from './breakpoints';

/**
 * Viewport queries.
 *
 * Design-system tiers are phone (≤767px) and below desktop (≤1023px). See
 * `breakpoints.ts`. `STACK_MEDIA` and `BELOW_XL_MEDIA` are older layout
 * queries (Tailwind `sm` and `xl`) used by analytics and the inbox. They are
 * not extra design-system breakpoints — do not add new cutoff widths.
 */
export const STACK_MEDIA = '(max-width: 639px)';
export const TABLET_PORTRAIT_MEDIA = PHONE_MEDIA;
export const DENSITY_MEDIA = BELOW_DESKTOP_MEDIA;
/** Analytics stays on a 6-column reflow until Tailwind `xl` (1280px). */
export const BELOW_XL_MEDIA = '(max-width: 1279px)';
/**
 * Laptop-height windows after 200% zoom (WCAG 1.4.4). Multi-pane pages collapse
 * and the shell scrolls so lists are not trapped under the page chrome.
 */
export const SHORT_VIEWPORT_MEDIA = '(max-height: 40rem)';

/** Settle layout/chart updates after the last resize frame. */
export const VIEWPORT_LAYOUT_DEBOUNCE_MS = 150;

/** 44px below lg (WCAG AAA target size); 40px at lg+. */
export const CONTROL_HEIGHT_CLASS = 'h-11 lg:h-10';
/** Square hit target matching icon buttons — 44px below lg, 40px at lg+. */
export const CONTROL_HIT_SQUARE_CLASS = 'h-11 w-11 lg:h-10 lg:w-10';
/** Min height only — for table sort headers and inline links that should grow with wrapping text. */
export const TABLE_HIT_TARGET_CLASS = 'inline-flex min-h-11 items-center lg:min-h-10';
/** Height plus 16px text below lg (avoids iOS input zoom). */
export const CONTROL_SIZE_CLASS = 'h-11 text-body-m lg:h-10';
/** Fixed width at a Tailwind breakpoint, e.g. `sm:w-64` or `lg:w-[220px]`. */
const RESPONSIVE_FIXED_WIDTH =
  /(?:^|\s)((?:sm|md|lg|xl|2xl):w-(?!full\b)(?:\[[^\]]+\]|[^\s]+))/g;

/**
 * How the wrapper of a labelled Input/NativeSelect is sized, as data attributes the two field
 * modules style (`[data-width]`, `[data-shrink-from]`). Forms fill the container; toolbar widths
 * (`w-44`, `w-64`, `sm:w-64`, ...) stay shrink-wrapped from the breakpoint that sets them, so flex
 * toolbars share a row instead of stacking full-width fields.
 */
export interface FieldChromeWidth {
  /** `full`: 100%. `shrink`: the consumer sets a fixed width, the wrapper keeps its content size. `flex`: fills a flex row. */
  mode: 'full' | 'shrink' | 'flex';
  /** `min-w-0` came with `w-full`: let the wrapper shrink inside a flex row. */
  minWidthZero: boolean;
  /** Breakpoints (`sm`, `lg`, ...) from which a `w-full` wrapper turns shrink-wrapped. */
  shrinkFrom: string[];
}

export type FieldChromeWidthAttributes = {
  'data-width': FieldChromeWidth['mode'];
  'data-min-w-0'?: '';
  'data-shrink-from'?: string;
};

export function fieldChromeWidth(className?: string): FieldChromeWidth {
  const source = className ?? '';
  if (/\bflex-1\b/.test(source)) return { mode: 'flex', minWidthZero: true, shrinkFrom: [] };

  const responsiveFixed = [...source.matchAll(RESPONSIVE_FIXED_WIDTH)].map((match) => match[1]!);
  // `w-full sm:w-64` must not keep the wrapper at `w-full` above `sm`: that flex
  // item would take the whole toolbar row and push the next filter onto its own line.
  if (/\bw-full\b/.test(source) && responsiveFixed.length > 0) {
    return {
      mode: 'full',
      minWidthZero: /\bmin-w-0\b/.test(source),
      shrinkFrom: [...new Set(responsiveFixed.map((token) => token.slice(0, token.indexOf(':'))))],
    };
  }

  if (/\bw-full\b/.test(source)) return { mode: 'full', minWidthZero: false, shrinkFrom: [] };
  if (/\bw-(?!full\b)/.test(source)) return { mode: 'shrink', minWidthZero: false, shrinkFrom: [] };
  return { mode: 'full', minWidthZero: false, shrinkFrom: [] };
}

export function fieldChromeWidthAttributes(width: FieldChromeWidth): FieldChromeWidthAttributes {
  return {
    'data-width': width.mode,
    ...(width.minWidthZero ? { 'data-min-w-0': '' as const } : {}),
    ...(width.shrinkFrom.length > 0 ? { 'data-shrink-from': width.shrinkFrom.join(' ') } : {}),
  };
}
